import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import SearchPage from './pages/SearchPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CartDrawer from './components/CartDrawer';
import AccountPage from './pages/AccountPage';
import OrdersPage from './pages/OrdersPage';
import OrderStatusPage from './pages/OrderStatusPage';
import TrackOrderPage from './pages/TrackOrderPage';
import AccountDetailsPage from './pages/AccountDetailsPage';
import AddressesPage from './pages/AddressesPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import CorporateGiftingPage from './pages/CorporateGiftingPage';
import CollectionsPage from './pages/CollectionsPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import NotFoundPage from './pages/NotFoundPage';
import ReviewPage from './pages/ReviewPage';
import PolicyPage from './pages/PolicyPage';
import ContactPage from './pages/ContactPage';
import VerticalGardenPage from './pages/VerticalGardenPage';
import BundlePage from './pages/BundlePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import { getStoredUser, logout as apiLogout, onTokenExpired, validateCoupon, syncCart, clearServerCart, getSiteSettings } from './lib/api';
import { configureMetaPixel, trackPageView, trackEvent } from './lib/metaPixel';
import { trackVisit, trackAddToCart } from './lib/analytics';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Fires a Meta Pixel PageView on every client-side route change. The very
// first PageView is already sent when the pixel initializes (see the
// configureMetaPixel call in App below), so this skips that first mount.
function MetaPixelPageViews() {
  const { pathname } = useLocation();
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    trackPageView();
  }, [pathname]);
  return null;
}

// Sends a hit to our own analytics endpoint (powers the admin Analytics page
// and live visitor map) on first load, every route change, and a periodic
// heartbeat so a visitor who stays on one page still shows up as "live".
function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => { trackVisit(pathname); }, [pathname]);
  useEffect(() => {
    const id = setInterval(() => trackVisit(pathname, { heartbeat: true }), 20_000);
    return () => clearInterval(id);
  }, [pathname]);
  return null;
}

function WhatsAppButton() {
  const { pathname } = useLocation();
  const hidden = pathname === '/checkout' || pathname === '/order-confirmation';
  if (hidden) return null;
  return (
    <a
      href="https://wa.me/917696999018"
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-fab"
      aria-label="Chat with us on WhatsApp"
    >
      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
}

// Persisted across refreshes — same nq_ localStorage convention used for auth
const CART_KEY   = 'nq_cart';
const COUPON_KEY = 'nq_coupon';

function getStoredCart() {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const arr = Array.isArray(parsed) ? parsed : [];
    // Migrate old cart items that pre-date the cartItemId field so they still
    // render correctly (remove/updateQty will work via the fallback `|| item.id`).
    return arr.map(item => {
      if (item.cartItemId) return item;
      const base = item._id || item.id || '';
      const size    = item._selectedSize    || '';
      const planter = item._selectedPlanter || '';
      const color   = item._selectedColor   || '';
      const cartItemId = [base, size, planter, color].filter(Boolean).join('||');
      return { ...item, cartItemId };
    });
  } catch {
    return [];
  }
}

function getStoredCoupon() {
  try {
    const raw = localStorage.getItem(COUPON_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function App() {
  const [cart, setCart] = useState(() => getStoredCart());
  const [cartOpen, setCartOpen] = useState(false);
  const [user, setUser] = useState(() => getStoredUser());

  // Lifted coupon state — shared between CartDrawer, CartPage and CheckoutPage
  const [appliedCoupon, setAppliedCoupon] = useState(() => getStoredCoupon());
  // appliedCoupon shape: { code, label, discountAmount, desc, minOrder }

  // Keep cart in sync with localStorage so it survives a page refresh
  useEffect(() => {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    } catch {
      // ignore storage errors (e.g. quota exceeded, private mode)
    }
  }, [cart]);

  // Keep applied coupon in sync with localStorage so it survives a page refresh
  useEffect(() => {
    try {
      if (appliedCoupon) {
        localStorage.setItem(COUPON_KEY, JSON.stringify(appliedCoupon));
      } else {
        localStorage.removeItem(COUPON_KEY);
      }
    } catch {
      // ignore storage errors
    }
  }, [appliedCoupon]);

  // Re-sync cart state when BundlePage (or any tab) writes directly to localStorage
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === CART_KEY) {
        setCart(getStoredCart());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Meta Pixel — pull the admin-configured Pixel ID/enabled flag once on load.
  // Safe to fire-and-forget: configureMetaPixel/trackEvent are no-ops until
  // a valid, enabled Pixel ID actually comes back.
  useEffect(() => {
    getSiteSettings()
      .then(settings => configureMetaPixel(settings?.metaPixel))
      .catch(() => { /* tracking is best-effort — never block the app on it */ });
  }, []);

  // Auto-logout when JWT expires (2h window)
  useEffect(() => {
    onTokenExpired(() => {
      setUser(null);
      setCart([]);
      setAppliedCoupon(null);
    });
  }, []);

  // Build a unique cart-line key from the product id + selected variant options.
  // This mirrors Shopify's behaviour: the same product in different sizes/
  // planters/colours becomes separate line items in the cart.
  const buildCartItemId = (product) => {
    const base = product._id || product.id;
    const size    = product._selectedSize    || '';
    const planter = product._selectedPlanter || '';
    const color   = product._selectedColor   || '';
    return [base, size, planter, color].filter(Boolean).join('||');
  };

  const addToCart = (product, forcedQty) => {
    // Bundle items get a stable cart ID keyed by bundleId so each bundle
    // is its own unique line. Regular products use the existing id+variant key.
    const cartItemId = product.type === 'bundle'
      ? `bundle||${product.bundleId}`
      : buildCartItemId(product);
    const addQty = forcedQty || product._qty || 1;
    setCart(prev => {
      const existing = prev.find(i => i.cartItemId === cartItemId);
      if (existing) {
        return prev.map(i =>
          i.cartItemId === cartItemId ? { ...i, qty: i.qty + addQty } : i
        );
      }
      return [...prev, { ...product, cartItemId, qty: addQty }];
    });
    setCartOpen(true);
    trackEvent('AddToCart', {
      content_ids: [String(product._id || product.id || '')],
      content_name: product.name || '',
      content_type: 'product',
      value: (product.price || 0) * addQty,
      currency: 'INR',
    });
    trackAddToCart({ productId: String(product._id || product.id || ''), qty: addQty });
  };

  // updateQty and removeFromCart now operate on cartItemId (the unique line key)
  const updateQty = (cartItemId, qty) => {
    if (qty < 1) return removeFromCart(cartItemId);
    setCart(prev => prev.map(i => i.cartItemId === cartItemId ? { ...i, qty } : i));
  };

  const removeFromCart = (cartItemId) => setCart(prev => prev.filter(i => i.cartItemId !== cartItemId));
  const clearCart = () => { setCart([]); setAppliedCoupon(null); };

  // ── Abandoned-cart sync ──────────────────────────────────────────────────
  // Push cart state to the server 3 s after the user stops making changes.
  // Only fires for logged-in users — guests are not tracked.
  const syncTimerRef = useRef(null);
  const syncCartToServer = useCallback((items, step = 'cart') => {
    if (!user) return;           // guest — nothing to track
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      syncCart(items, step).catch(() => { /* silent — don't disrupt UX */ });
    }, 3000);
  }, [user]);

  useEffect(() => {
    syncCartToServer(cart, 'cart');
    return () => clearTimeout(syncTimerRef.current);
  }, [cart, user]);

  // After an order is placed (clearCart called from checkout/confirmation),
  // also remove the server-side record.
  const clearCartAfterOrder = useCallback(() => {
    clearCart();
    if (user) clearServerCart().catch(() => {});
  }, [user]);
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartTotal = cart.reduce((s, i) => s + i.price * i.qty, 0);

  // Re-validate the applied coupon whenever the cart total changes (items added/removed,
  // or restored from a previous session on refresh) — silently drop it if it's no longer
  // valid (expired, usage limit hit, minimum order no longer met, etc).
  useEffect(() => {
    if (!appliedCoupon) return;
    if (cart.length === 0) { setAppliedCoupon(null); return; }
    let cancelled = false;
    validateCoupon(appliedCoupon.code, cartTotal)
      .then(result => { if (!cancelled) setAppliedCoupon({ ...result, desc: result.desc || '' }); })
      .catch(() => { if (!cancelled) setAppliedCoupon(null); });
    return () => { cancelled = true; };
  }, [cartTotal, cart.length]);

  const handleLogin = (userData) => setUser(userData);
  const handleLogout = () => { apiLogout(); setUser(null); };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <MetaPixelPageViews />
      <AnalyticsTracker />
      <Navbar cartCount={cartCount} onCartClick={() => setCartOpen(true)} user={user} onLogout={handleLogout} />
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        updateQty={updateQty}
        removeFromCart={removeFromCart}
        total={cartTotal}
        appliedCoupon={appliedCoupon}
        setAppliedCoupon={setAppliedCoupon}
      />
      <Routes>
        <Route path="/" element={<HomePage addToCart={addToCart} />} />
        <Route path="/search" element={<SearchPage addToCart={addToCart} />} />
        <Route path="/product/:slug" element={<ProductDetailPage addToCart={addToCart} />} />
        <Route
          path="/cart"
          element={
            <CartPage
              cart={cart}
              updateQty={updateQty}
              removeFromCart={removeFromCart}
              total={cartTotal}
              addToCart={addToCart}
              appliedCoupon={appliedCoupon}
              setAppliedCoupon={setAppliedCoupon}
            />
          }
        />
        <Route
          path="/checkout"
          element={
            <CheckoutPage
              cart={cart}
              total={cartTotal}
              clearCart={clearCartAfterOrder}
              user={user}
              appliedCoupon={appliedCoupon}
              setAppliedCoupon={setAppliedCoupon}
            />
          }
        />
        <Route
          path="/account"
          element={<AccountPage onLogin={handleLogin} user={user} />}
        />
        <Route
          path="/account/orders"
          element={<OrdersPage user={user} onLogout={handleLogout} />}
        />
        <Route
          path="/account/orders/:orderId"
          element={<OrderStatusPage user={user} onLogout={handleLogout} />}
        />
        <Route
          path="/account/details"
          element={<AccountDetailsPage user={user} onLogout={handleLogout} onUserUpdate={handleLogin} />}
        />
        <Route
          path="/account/addresses"
          element={<AddressesPage user={user} onLogout={handleLogout} />}
        />
        <Route
          path="/order-confirmation"
          element={<OrderConfirmationPage user={user} clearCart={clearCartAfterOrder} />}
        />
        <Route path="/track-order" element={<TrackOrderPage />} />
        <Route path="/track-order/:slug" element={<TrackOrderPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/corporate-gifting" element={<CorporateGiftingPage />} />
        <Route path="/collections" element={<CollectionsPage addToCart={addToCart} />} />
        <Route path="/collections/:slug" element={<CollectionsPage addToCart={addToCart} />} />
        <Route path="/policies/:slug" element={<PolicyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/vertical-garden" element={<VerticalGardenPage />} />
        <Route path="/bundle/:slug" element={<BundlePage addToCart={addToCart} />} />
        <Route path="/review/:slug" element={<ReviewPage user={user} />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <Footer />
      <WhatsAppButton />
    </BrowserRouter>
  );
}